import { useSiteDataOnClient } from '@/composables/useSiteDataOnClient';
import { prepareQueryString } from '#shared/utils/queryString';
import { getSessionCookiesOnServer } from '#server/utils';
import { triggerRouteChange } from '@/utils';

export default defineNuxtRouteMiddleware((to) => {
  // Prevent routes except page routes to execute the middleware
  const is404 = (to.name as string)?.split?.('___')[0] === '404';
  const [cleanPath = ''] = to.fullPath.split('?');
  const isApiPath = !to.fullPath.startsWith('/api/');
  const isPageRoute = isApiPath &&
    !cleanPath.startsWith('/_nuxt/') &&
    !cleanPath.startsWith('/__nuxt') &&
    !/\.\w+$/.test(cleanPath);

  // Do nothing
  if (is404) return;

  // Redirect to 404 page if route is not a page route
  if (!isPageRoute) return navigateTo('/404');

  const { siteData, currentPage } = useSiteDataOnClient();

  // Redirect to 404 if page data is not found
  if (!siteData?.value || !Object.keys(siteData.value).length) {
    triggerRouteChange('404');
    return navigateTo('/404');
  }

  const { pageType: page, slug, lang, query, hash } = getBaseUrl(to.fullPath);
  const event = useRequestEvent();

  const cookies = event
    ? getSessionCookiesOnServer(event)
    : getSessionCookiesOnClient();
  
  const routerName = (to.name as string || '')?.split?.('__')?.[0] as string;
  const firstPage = getFirstPage(siteData.value, getSessionCookiesOnClient());
  const pathName = (routerName === 'slug' ? page || firstPage : routerName) as PageType;
  const queryString = prepareQueryString(query, hash, cookies);
  const isPageViewable = canViewable({ pageType: pathName, siteData: siteData.value, cookies });
  const isUserAlreadySubscribed = siteData.value?.isUserAlreadySubscribed;
  const bypassRegister = !!siteData.value?.bypassRegister;
  const availablePages = siteData.value?.availablePages || cookies?.[COOKIE.AVAILABLE_PAGES] as string[];

  // Do noting if the page is not viewable
  if (siteData.value?.maintenance || siteData.value?.noparams) return;

  if (siteData.value?.redirectTo) {
    return navigateTo(siteData.value?.redirectTo, { external: true });
  }

  if (isUserAlreadySubscribed) {
    return handleUserAlreadySubscribedError(siteData.value?.subscriberId, slug)
  }

  if ((!currentPage.value || !availablePages.includes(pathName)) && !siteData.value?.maintenance && !siteData.value?.noparams) {
    if (!isApiPath) {
      triggerRouteChange('404');
      return navigateTo('/404');
    }
  }

  if (!isPageViewable) {
    return navigateTo('/'+ [slug, lang, firstPage].filter(Boolean).join('/') + queryString);
  }

  if (pathName === PageType.PAYMENT_SUCCESS && !(siteData.value.preview && !siteData.value.sandbox) && !isUserAlreadySubscribed) {
    // If subscriberId is not in the cookies, payment success page will be redirected to payment
    const subscriberId = useCookie(COOKIE.SUBSCRIBER_ID, { sameSite: 'none', secure: true, path: `/${slug}` });
    if (!subscriberId.value) {
      const paymentPageUrl = '/' + [slug, lang, PageType.PAYMENT].filter(Boolean).join('/');
      return navigateTo(paymentPageUrl + queryString);
    }
  }

  const isFirstPage = pathName === firstPage;
  const pagePath = !isFirstPage || (isFirstPage && bypassRegister && [PageType.PAYMENT, PageType.PAYWALL].includes(firstPage)) ? pathName : '';
  const redirectTo = '/' + [slug, lang, pagePath].filter(Boolean).join('/') + queryString;

  triggerRouteChange(pathName);

  if (redirectTo !== to.fullPath) {
    return navigateTo(redirectTo);
  }
});
